/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import {
  HStack,
  IconButton,
  Icon,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Grid,
  Button,
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

interface MonthNavigatorProps {
  currentDate: moment.Moment;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  // eslint-disable-next-line no-unused-vars
  onSelectMonth: (year: number, month: number) => void;
  isDisabled?: boolean;
  compact?: boolean;
}

export function MonthNavigator({
  currentDate,
  onPrevMonth,
  onNextMonth,
  onSelectMonth,
  isDisabled = false,
  compact = false,
}: MonthNavigatorProps) {
  const monthYear = currentDate.format('MMMM [de] YYYY');
  const isCurrentMonth = currentDate.isSame(moment(), 'month');
  const [selectedYear, setSelectedYear] = useState(currentDate.year());

  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];

  const currentYear = moment().year();
  const currentMonth = moment().month();

  const handleYearChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setSelectedYear(prev => prev - 1);
    } else if (direction === 'next' && selectedYear < currentYear) {
      setSelectedYear(prev => prev + 1);
    }
  };

  const isMonthDisabled = (monthIndex: number) => {
    return selectedYear === currentYear && monthIndex > currentMonth;
  };

  const isMonthSelected = (monthIndex: number) => {
    return selectedYear === currentDate.year() && monthIndex === currentDate.month();
  };

  return (
    <HStack
      spacing={compact ? 1 : 2}
      bg="white"
      px={compact ? 2 : 4}
      py={compact ? 0 : 2}
      h={compact ? '32px' : 'auto'}
      borderRadius="lg"
      border={compact ? '1px solid' : undefined}
      borderColor={compact ? 'gray.200' : undefined}
      shadow={compact ? 'none' : 'sm'}
      position="relative"
      opacity={isDisabled ? 0.4 : 1}
      filter={isDisabled ? 'blur(1px)' : 'none'}
      pointerEvents={isDisabled ? 'none' : 'auto'}
      transition="all 0.3s ease"
      title={isDisabled ? 'Selecione periodo mensal para alterar o mes' : undefined}
    >
      <IconButton
        aria-label="Mes anterior"
        icon={<Icon as={ChevronLeft} boxSize={compact ? 3.5 : undefined} />}
        onClick={onPrevMonth}
        variant="ghost"
        size={compact ? 'xs' : 'sm'}
        colorScheme="blue"
        isDisabled={isDisabled}
      />
      <Popover placement="bottom" isOpen={isDisabled ? false : undefined}>
        <PopoverTrigger>
          <HStack
            spacing={1.5}
            minW={compact ? 'auto' : '180px'}
            justify="center"
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            _hover={{ bg: isDisabled ? 'white' : 'gray.50' }}
            px={compact ? 1 : 2}
            py={compact ? 0 : 1}
            borderRadius="md"
          >
            <Icon
              as={Calendar}
              color={isDisabled ? 'gray.300' : 'gray.500'}
              boxSize={compact ? 3.5 : 4}
            />
            <Text
              fontWeight="medium"
              fontSize={compact ? 'xs' : 'sm'}
              textTransform="capitalize"
              color={isDisabled ? 'gray.400' : 'gray.700'}
              whiteSpace="nowrap"
            >
              {monthYear}
            </Text>
          </HStack>
        </PopoverTrigger>
        <PopoverContent width="280px">
          <PopoverBody p={4}>
            <HStack justify="space-between" mb={4}>
              <IconButton
                aria-label="Ano anterior"
                icon={<Icon as={ChevronLeft} boxSize={4} />}
                onClick={() => handleYearChange('prev')}
                variant="ghost"
                size="sm"
              />
              <Text fontWeight="bold" fontSize="lg">
                {selectedYear}
              </Text>
              <IconButton
                aria-label="Proximo ano"
                icon={<Icon as={ChevronRight} boxSize={4} />}
                onClick={() => handleYearChange('next')}
                variant="ghost"
                size="sm"
                isDisabled={selectedYear >= currentYear}
              />
            </HStack>
            <Grid templateColumns="repeat(4, 1fr)" gap={2}>
              {months.map((month, index) => (
                <Button
                  key={month}
                  size="sm"
                  variant={isMonthSelected(index) ? 'solid' : 'ghost'}
                  colorScheme={isMonthSelected(index) ? 'blue' : 'gray'}
                  isDisabled={isMonthDisabled(index)}
                  onClick={() => onSelectMonth(selectedYear, index)}
                  fontSize="xs"
                >
                  {month}
                </Button>
              ))}
            </Grid>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <IconButton
        aria-label="Proximo mes"
        icon={<Icon as={ChevronRight} boxSize={compact ? 3.5 : undefined} />}
        onClick={onNextMonth}
        variant="ghost"
        size={compact ? 'xs' : 'sm'}
        colorScheme="blue"
        isDisabled={isDisabled || isCurrentMonth}
      />
    </HStack>
  );
}
